import React, { useState, useEffect } from "react";
import { Drawer, Descriptions, Badge, Button, Space, Tabs, Modal, Form, Input, Select, message, Tag, Card, Typography, Divider, List, Alert } from "antd";
import { EditOutlined, CloseOutlined, CloseCircleOutlined, ReloadOutlined, RobotOutlined, EyeOutlined, LinkOutlined, ScissorOutlined, FileTextOutlined, ContainerOutlined, DownOutlined, StopOutlined, CheckCircleOutlined, HistoryOutlined } from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { formatDateTime } from "../../../../utils/dateUtils";
import { settlementService } from "../../../../api/enhancedApi";
import settlementRevisionApi from "../../../../api/settlementRevisionApi";
import { INVOICE_DATA_SOURCE_STATUS_LABELS, INVOICE_DATA_SOURCE_STATUS_COLORS, SOURCE_TYPE_LABELS, SOURCE_TYPE_COLORS, STANDARD_COST_TYPE_LABELS, PARSING_STATUS_LABELS, PARSING_STATUS_COLORS, STRUCTURE_STATUS_LABELS, STRUCTURE_STATUS_COLORS, idsHasReachedStep, idsIsTerminal } from "../../constants";
import { getActionItems } from "../../../../api/actionItemApi";
import { getPresignedViewUrl } from "../../../../api/attachmentApi";
import InvoiceDataSourceActionMenu from "./InvoiceDataSourceActionMenu";
import ValidationSessionPanel from "./ValidationSessionPanel";
import ProcessingHistoryPanel from "./ProcessingHistoryPanel";
import PermissionGuard from "../../../../components/auth/PermissionGuard";
import VoidConfirmModal from "../../APReconciliationWorkbench/components/revision/VoidConfirmModal";
import { getReplacementId, getReplacementLabel, getReplacesId, getReplacesLabel, getVoidReason } from "../../utils/revisionDisplay";

const { Text } = Typography;

const { TextArea } = Input;
const { Option } = Select;

const InvoiceDataSourceDetailDrawer = ({ open, onClose, record, onUpdate }) => {
    const [activeTab, setActiveTab] = useState("basic");
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [voidModal, setVoidModal] = useState({ open: false, impact: null });
    const [voidImpactLoading, setVoidImpactLoading] = useState(false);
    const [voidSubmitting, setVoidSubmitting] = useState(false);


    // 关联文件列表
    const [groupMembers, setGroupMembers] = useState([]);
    const [loadingGroupMembers, setLoadingGroupMembers] = useState(false);

    // 加载关联文件列表（useEffect必须在任何条件返回之前调用）
    useEffect(() => {
        if (record?.file_group_id && open) {
            loadGroupMembers();
        }
    }, [record?.file_group_id, open]);

    if (!record) return null;

    const loadGroupMembers = async () => {
        setLoadingGroupMembers(true);
        try {
            // 调用API查询同组文件（需要后端支持按file_group_id查询）
            const response = await settlementService.getInvoiceDataSources({
                // 这里暂时无法实现，需要后端添加按file_group_id过滤的支持
                // 先留空，等后端实现后再完善
            });
            // setGroupMembers(response.data?.items || []);
        } catch (error) {
            console.error("加载关联文件失败:", error);
        } finally {
            setLoadingGroupMembers(false);
        }
    };

    // 解析当前状态的两个维度
    const getParsingStatus = (status) => {
        // step >= 1 表示已经过了 OCR 阶段
        if (idsHasReachedStep(status, 1)) {
            return "OCR_COMPLETED";
        }
        // 仍在 OCR 阶段或初始状态，原样返回
        return status;
    };

    const getStructureStatus = (status) => {
        // 根据主状态推断结构化状态 (V4.3)
        const statusMap = {
            RECEIVED: "NOT_STARTED",
            OCR_IN_PROGRESS: "NOT_STARTED",
            OCR_COMPLETED: "NOT_STARTED",
            OCR_FAILED: "NOT_STARTED",
            STRUCTURE_IN_PROGRESS: "STRUCTURE_IN_PROGRESS",
            STRUCTURE_COMPLETED: "STRUCTURE_COMPLETED",
            STRUCTURE_FAILED: "STRUCTURE_FAILED",
            ALLOCATION_IN_PROGRESS: "STRUCTURE_COMPLETED",
            ALLOCATION_COMPLETED: "STRUCTURE_COMPLETED",
            ALLOCATION_FAILED: "STRUCTURE_COMPLETED",
            VALIDATION_IN_PROGRESS: "STRUCTURE_COMPLETED",
            VALIDATION_COMPLETED: "STRUCTURE_COMPLETED",
            BATCH_VALIDATION_IN_PROGRESS: "STRUCTURE_COMPLETED",
            BATCH_VALIDATION_COMPLETED: "STRUCTURE_COMPLETED",
            BATCH_VALIDATION_FAILED: "STRUCTURE_COMPLETED",
            CONFIRMED: "STRUCTURE_COMPLETED",
            REJECTED: "STRUCTURE_COMPLETED",
            VOIDED: "STRUCTURE_COMPLETED",
        };
        return statusMap[status] || "NOT_STARTED";
    };

    // 状态映射 — 从注册表获取颜色，避免硬编码
    const getStatusBadge = (status) => {
        const label = INVOICE_DATA_SOURCE_STATUS_LABELS[status] || status;
        const color = INVOICE_DATA_SOURCE_STATUS_COLORS[status] || "default";
        // 将 Tag 颜色映射为 Badge status（Badge 只支持有限的 status 值）
        const badgeStatusMap = {
            default: "default", processing: "processing", cyan: "success",
            error: "error", warning: "warning", success: "success",
        };
        return <Badge status={badgeStatusMap[color] || "default"} text={label} />;
    };

    // 解析状态徽章
    const getParsingStatusBadge = (status) => {
        const label = PARSING_STATUS_LABELS[status] || status;
        const color = PARSING_STATUS_COLORS[status] || "default";
        return <Tag color={color}>{label}</Tag>;
    };

    // 结构化状态徽章
    const getStructureStatusBadge = (status) => {
        const label = STRUCTURE_STATUS_LABELS[status] || status;
        const color = STRUCTURE_STATUS_COLORS[status] || "default";
        return <Tag color={color}>{label}</Tag>;
    };

    // 来源类型映射 - 使用统一的常量
    const getSourceTypeBadge = (type) => {
        const label = SOURCE_TYPE_LABELS[type] || type;
        const color = SOURCE_TYPE_COLORS[type] || "default";
        return <Badge color={color} text={label} />;
    };

    // 查看原文件（通过预签名链接）
    const handleViewOriginalFile = async () => {
        if (!record) return;
        try {
            let objectKey = null;
            const raw = record.raw_file_path;
            if (!raw) {
                message.warning("未找到原文件路径");
                return;
            }
            // 若为完整URL，则解析出路径作为objectKey；否则直接视为objectKey
            if (typeof raw === "string" && /^https?:\/\//i.test(raw)) {
                try {
                    const u = new URL(raw);
                    objectKey = decodeURIComponent(u.pathname.replace(/^\//, ""));
                } catch (e) {
                    objectKey = raw.replace(/^\//, "");
                }
            } else {
                objectKey = String(raw).replace(/^\//, "");
            }

            if (!objectKey) {
                message.warning("无法解析原文件路径");
                return;
            }

            const data = await getPresignedViewUrl(objectKey);
            if (data?.signed_url) {
                window.open(data.signed_url, "_blank", "noopener,noreferrer");
            } else {
                throw new Error("未返回预签名链接");
            }
        } catch (error) {
            console.warn("获取原文件预签名链接失败:", error);
            // 回退：若raw_file_path是完整URL，尝试直接打开
            const raw = record?.raw_file_path;
            if (raw && /^https?:\/\//i.test(raw)) {
                window.open(raw, "_blank", "noopener,noreferrer");
            } else {
                message.error("无法打开原文件链接");
            }
        }
    };

    // 编辑结构化数据
    const handleEditStructuredData = () => {
        form.setFieldsValue({
            structured_data: JSON.stringify(record.structured_data || {}, null, 2),
        });
        setEditModalVisible(true);
    };

    // 保存结构化数据
    const handleSaveStructuredData = async () => {
        try {
            const values = await form.validateFields();
            const parsedData = JSON.parse(values.structured_data);

            const response = await settlementService.updateInvoiceDataSource({ id: record.id, data: { structured_data: parsedData } });
            if (response.data?.success) {
                message.success("制表校验数据更新成功");
                setEditModalVisible(false);
                if (onUpdate) onUpdate();
            } else {
                message.error("保存失败: " + (response.data?.message || "未知错误"));
            }
        } catch (error) {
            if (error.name === "SyntaxError") {
                message.error("JSON 格式错误，请检查语法");
            } else {
                message.error("保存失败: " + (error.response?.data?.detail || error.message));
            }
        }
    };

    // 查看对账审核 — 跳转到工作台
    const handleViewWorkbench = () => {
        if (!record?.id) return;
        if (!record.batch_number || !record.vendor_id) {
            message.warning("该账单缺少批次信息，无法打开工作台");
            return;
        }
        const params = new URLSearchParams({
            batch: record.batch_number,
            vendor: record.vendor_id,
            costType: record.standard_cost_type || "",
        });
        window.open(`/settlement/ap-workbench?${params.toString()}`, "_blank");
    };

    const handleOpenRelatedIds = (id) => {
        if (!id || !onUpdate) return;
        onUpdate({ action: "view", id });
    };

    const handleOpenVoidWithReparse = async () => {
        if (record.status !== "CONFIRMED") {
            message.warning("只有已审核账单可以作废并重发");
            return;
        }
        setVoidImpactLoading(true);
        try {
            const response = await settlementRevisionApi.assessImpact(record.id, "IDS");
            setVoidModal({ open: true, impact: response.data });
        } catch (error) {
            // 后端 RevisionError 的信息在 data.error；data.detail 是 HTTPException(500) 的字段
            const detail = error?.response?.data?.error || error?.response?.data?.detail || error.message || "影响评估失败";
            message.error(detail);
        } finally {
            setVoidImpactLoading(false);
        }
    };

    const handleConfirmVoidWithReparse = async (reason, mode) => {
        const selectedMode = mode || "copy";
        setVoidSubmitting(true);
        try {
            const response = await settlementRevisionApi.voidInvoiceWithReparse(record.id, reason, selectedMode);
            const newIdsId = response.data?.new_ids_id;
            const successMessage = (response.data?.mode || selectedMode) === "copy"
                ? "已作废，新账单已创建并复制了原数据，请前往审核并修改错误项"
                : newIdsId ? `已作废，新账单 ${newIdsId} 已创建，请前往处理` : "已作废并创建新账单";
            message.success(successMessage);
            setVoidModal({ open: false, impact: null });
            onUpdate?.();
        } catch (error) {
            const detail = error?.response?.data?.error || error?.response?.data?.detail || error.message || "作废并重发失败";
            message.error(detail);
        } finally {
            setVoidSubmitting(false);
        }
    };

    // Markdown内容
    const isReadOnly = idsIsTerminal(record.status);
    const voidReason = getVoidReason(record);
    const replacementId = getReplacementId(record);
    const replacementLabel = getReplacementLabel(record);
    const replacesId = getReplacesId(record);
    const replacesLabel = getReplacesLabel(record);

    let markdownContent = record.structured_data?.ocr_result?.markdown_results;
    if (Array.isArray(markdownContent)) {
        markdownContent = markdownContent.join("\n\n");
    }
    if (typeof markdownContent !== "string") {
        markdownContent = null;
    }

    const tabItems = [
        {
            key: "basic",
            label: "基本信息",
            children: (
                <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="ID">{record.id}</Descriptions.Item>
                    <Descriptions.Item label="来源类型">{getSourceTypeBadge(record.source_type)}</Descriptions.Item>
                    <Descriptions.Item label="原文件">
                        <Space>
                            <span>{record.original_filename || "原始文件"}</span>
                            {record.record_type === "FILE_GROUP" ? (
                                <Tag color="orange">文件组（{record.group_files?.length || 0}个文件）</Tag>
                            ) : record.raw_file_path ? (
                                <Button type="link" icon={<EyeOutlined />} onClick={handleViewOriginalFile}>
                                    查看原文件
                                </Button>
                            ) : (
                                <Text type="secondary">无文件</Text>
                            )}
                        </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="制表校验版本">
                        v{record.parser_version}
                        {record.is_active_parser && <Badge status="success" style={{ marginLeft: 8 }} text="当前版本" />}
                    </Descriptions.Item>
                    {/* V4.4 新增：关联机场 */}
                    {record.airport_code && (
                        <Descriptions.Item label="关联机场">
                            <Tag color="blue">{record.airport_code}</Tag>
                            {record.airport_name && <Text type="secondary" style={{ marginLeft: 8 }}>{record.airport_name}</Text>}
                        </Descriptions.Item>
                    )}
                    {/* ========== V4.2 新增：文件组记录展示 ========== */}
                    {record.record_type === "FILE_GROUP" && record.group_files && (
                        <Descriptions.Item label="包含的文件">
                            <List
                                size="small"
                                bordered
                                dataSource={record.group_files}
                                renderItem={(fileItem, index) => (
                                    <List.Item
                                        key={index}
                                        actions={[
                                            fileItem.object_key && (
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    icon={<EyeOutlined />}
                                                    onClick={async () => {
                                                        try {
                                                            const objectKey = fileItem.object_key.replace(/^\//, "");
                                                            const data = await getPresignedViewUrl(objectKey);
                                                            if (data?.signed_url) {
                                                                window.open(data.signed_url, "_blank", "noopener,noreferrer");
                                                            }
                                                        } catch (error) {
                                                            console.error("获取文件预览链接失败:", error);
                                                            message.error("无法预览文件");
                                                        }
                                                    }}
                                                >
                                                    查看
                                                </Button>
                                            ),
                                        ]}
                                    >
                                        <Space>
                                            <Text strong>{index + 1}.</Text>
                                            <Text>{fileItem.filename || "未知文件"}</Text>
                                            {fileItem.file_size && (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    ({(fileItem.file_size / 1024).toFixed(2)} KB)
                                                </Text>
                                            )}
                                        </Space>
                                    </List.Item>
                                )}
                                style={{ marginTop: 8 }}
                            />
                        </Descriptions.Item>
                    )}

                    {/* 关联组信息（旧格式兼容） */}
                    {record.file_group_id && record.record_type !== "FILE_GROUP" && (
                        <Descriptions.Item label="文件关联组">
                            <Space direction="vertical" size={4}>
                                <Space>
                                    <LinkOutlined style={{ color: "#1890ff" }} />
                                    <Text strong>{record.file_group_name || "未命名组"}</Text>
                                </Space>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    组ID: {record.file_group_id}
                                </Text>
                            </Space>
                        </Descriptions.Item>
                    )}

                    {/* 拆分文件信息 */}
                    {record.is_split && (
                        <Descriptions.Item label="拆分信息">
                            <Space direction="vertical" size={4}>
                                <Space>
                                    <ScissorOutlined style={{ color: "#52c41a" }} />
                                    <Text>此文件由拆分生成</Text>
                                </Space>
                                {record.split_from_filename && (
                                    <Space size={4}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            来源: {record.split_from_filename}
                                        </Text>
                                        {record.split_from_id && (
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<EyeOutlined />}
                                                onClick={() => {
                                                    // 关闭当前详情，打开原始文件的详情
                                                    onClose();
                                                    // 通过父组件的 onUpdate 回调触发查看原始文件
                                                    if (onUpdate) {
                                                        // 传递一个特殊标记，让父组件知道需要打开详情
                                                        onUpdate({ action: "view", id: record.split_from_id });
                                                    }
                                                }}
                                                style={{ padding: 0, height: "auto", fontSize: 12 }}
                                            >
                                                查看原始文件
                                            </Button>
                                        )}
                                    </Space>
                                )}
                                {record.split_pages && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        页面范围: 第 {record.split_pages} 页
                                    </Text>
                                )}
                            </Space>
                        </Descriptions.Item>
                    )}

                    <Descriptions.Item label="整体状态">{getStatusBadge(record.status)}</Descriptions.Item>
                    {record.status === "VOIDED" && voidReason && (
                        <Descriptions.Item label="作废原因">{voidReason}</Descriptions.Item>
                    )}
                    {record.status === "VOIDED" && replacementId && (
                        <Descriptions.Item label="替代新单">
                            <Button type="link" size="small" onClick={() => handleOpenRelatedIds(replacementId)} style={{ padding: 0 }}>
                                已被 {replacementLabel || replacementId} 替代
                            </Button>
                        </Descriptions.Item>
                    )}
                    {replacesId && (
                        <Descriptions.Item label="替代来源">
                            <Button type="link" size="small" onClick={() => handleOpenRelatedIds(replacesId)} style={{ padding: 0 }}>
                                替代了 {replacesLabel || replacesId}
                            </Button>
                        </Descriptions.Item>
                    )}
                    
                    {/* 如果是已拆分的父文件，显示提示信息 */}
                    {record.has_split_children && (
                        <Descriptions.Item label="说明" span={1}>
                            <Alert 
                                message="此文件为原始文件，已被拆分为多个子文件处理" 
                                description="系统不会对原始文件进行OCR识别，仅保留用于溯源。子文件会单独进行处理。"
                                type="info" 
                                showIcon 
                                style={{ marginTop: 8 }}
                            />
                        </Descriptions.Item>
                    )}
                    
                    <Descriptions.Item label="OCR 识别状态">{getParsingStatusBadge(getParsingStatus(record.status))}</Descriptions.Item>
                    <Descriptions.Item label="制表校验状态">{getStructureStatusBadge(getStructureStatus(record.status))}</Descriptions.Item>
                    <Descriptions.Item label="创建时间">{formatDateTime(record.created_at)}</Descriptions.Item>
                    <Descriptions.Item label="更新时间">{formatDateTime(record.updated_at)}</Descriptions.Item>
                </Descriptions>
            ),
        },
        // 关联文件Tab（仅当文件属于关联组时显示）
        ...(record.file_group_id
            ? [
                  {
                      key: "group_files",
                      label: (
                          <span>
                              <LinkOutlined /> 关联文件
                          </span>
                      ),
                      children: (
                          <Card size="small" title={`文件组: ${record.file_group_name || "未命名组"}`}>
                              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                                  <Text type="secondary">该数据源属于一个文件关联组，组内的文件会一起传递给AI进行联合识别。</Text>

                                  {/* TODO: 待后端添加按file_group_id查询的API支持后，显示同组其他文件 */}
                                  <Divider style={{ margin: "12px 0" }} />

                                  <div>
                                      <Text strong>当前文件（本文件）</Text>
                                      <Card size="small" style={{ marginTop: 8, backgroundColor: "#e6f7ff" }}>
                                          <Space direction="vertical" size={4}>
                                              <Text>{record.original_filename}</Text>
                                              <Text type="secondary" style={{ fontSize: 12 }}>
                                                  ID: {record.id}
                                              </Text>
                                              {record.is_split && (
                                                  <Tag icon={<ScissorOutlined />} color="success" size="small">
                                                      拆分文件
                                                  </Tag>
                                              )}
                                          </Space>
                                      </Card>
                                  </div>

                                  {/* 其他组成员（需要后端API支持） */}
                                  <div>
                                      <Text strong>其他组成员</Text>
                                      <div style={{ marginTop: 8 }}>
                                          {loadingGroupMembers ? (
                                              <Text type="secondary">加载中...</Text>
                                          ) : groupMembers.length > 0 ? (
                                              <List
                                                  size="small"
                                                  bordered
                                                  dataSource={groupMembers.filter((m) => m.id !== record.id)}
                                                  renderItem={(item) => (
                                                      <List.Item>
                                                          <Space direction="vertical" size={4}>
                                                              <Text>{item.original_filename}</Text>
                                                              <Text type="secondary" style={{ fontSize: 12 }}>
                                                                  ID: {item.id}
                                                              </Text>
                                                          </Space>
                                                      </List.Item>
                                                  )}
                                              />
                                          ) : (
                                              <Card size="small" style={{ backgroundColor: "#fafafa" }}>
                                                  <Text type="secondary">暂无法加载其他组成员（需要后端API支持按file_group_id查询）</Text>
                                              </Card>
                                          )}
                                      </div>
                                  </div>
                              </Space>
                          </Card>
                      ),
                  },
              ]
            : []),
        {
            key: "structured_data",
            label: "制表校验数据",
            children: (
                <Card
                    title="制表校验结果"
                    size="small"
                    extra={
                        <Button type="link" icon={<EditOutlined />} onClick={handleEditStructuredData} disabled={isReadOnly}>
                            编辑
                        </Button>
                    }
                >
                    {record.structured_data ? (
                        <pre
                            style={{
                                background: "#f5f5f5",
                                padding: "12px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                maxHeight: "400px",
                                overflow: "auto",
                            }}
                        >
                            {JSON.stringify(record.structured_data, null, 2)}
                        </pre>
                    ) : (
                        <Text type="secondary">暂无制表校验数据</Text>
                    )}
                </Card>
            ),
        },
        // Markdown预览Tab (如果有markdown_results)
        ...(markdownContent
            ? [
                  {
                      key: "markdown_preview",
                      label: "Markdown预览",
                      children: (
                          <Card title="OCR Markdown结果" size="small">
                              <div style={{ padding: 12, overflow: "auto", maxHeight: 600 }}>
                                  <div className="markdown-content">
                                      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{markdownContent}</ReactMarkdown>
                                  </div>
                              </div>
                          </Card>
                      ),
                  },
              ]
            : []),
        {
            key: "invoices",
            label: "关联账单",
            children: (
                <div>
                    {/* TODO: 显示关联的账单列表 */}
                    <Text type="secondary">暂无关联账单数据</Text>
                </div>
            ),
        },
        {
            key: "history",
            label: (
                <span>
                    <HistoryOutlined /> 处理历史
                </span>
            ),
            children: <ProcessingHistoryPanel record={record} onRefresh={onUpdate} />,
        },
        // 验证会话标签页（如果有验证数据）
        ...(record.structured_data?.validation_session
            ? [
                  {
                      key: "validation",
                      label: (
                          <span>
                              <CheckCircleOutlined /> 费用验证
                          </span>
                      ),
                      children: <ValidationSessionPanel validationSession={record.structured_data.validation_session} structuredData={record.structured_data} />,
                  },
              ]
            : []),
    ];

    return (
        <>
            <Drawer
                title="账单数据源详情"
                width={"70%"}
                open={open}
                onClose={onClose}
                extra={
                    <Space>
                        {record.status === "CONFIRMED" && (
                            <PermissionGuard permission="settlement:revision:write">
                                <Button danger icon={<CloseCircleOutlined />} onClick={handleOpenVoidWithReparse} loading={voidImpactLoading || voidSubmitting}>
                                    作废并重发
                                </Button>
                            </PermissionGuard>
                        )}
                        <InvoiceDataSourceActionMenu record={record} onSuccess={onUpdate} isReadOnly={isReadOnly} triggerType="button" />

                        <Button type="default" icon={<EyeOutlined />} onClick={handleViewWorkbench} disabled={!idsHasReachedStep(record.status, 2)}>
                            对账审核
                        </Button>
                    </Space>
                }
            >
                <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
            </Drawer>

            {/* 编辑制表校验数据模态框 */}
            <Modal title="编辑制表校验数据" width={800} open={editModalVisible} onOk={handleSaveStructuredData} onCancel={() => setEditModalVisible(false)} okText="保存" cancelText="取消">
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="制表校验数据 (JSON 格式)"
                        name="structured_data"
                        rules={[
                            { required: true, message: "请输入制表校验数据" },
                            {
                                validator: (_, value) => {
                                    try {
                                        JSON.parse(value);
                                        return Promise.resolve();
                                    } catch (error) {
                                        return Promise.reject(new Error("请输入有效的 JSON 格式"));
                                    }
                                },
                            },
                        ]}
                    >
                        <TextArea rows={20} placeholder="请输入 JSON 格式的制表校验数据" style={{ fontFamily: "monospace", fontSize: "12px" }} />
                    </Form.Item>
                </Form>
            </Modal>

            <VoidConfirmModal
                open={voidModal.open}
                title="作废并重发"
                targetLabel={record.original_filename || record.id}
                impact={voidModal.impact}
                impactLoading={voidImpactLoading}
                confirmText="确认作废并重发"
                submitting={voidSubmitting}
                onCancel={() => setVoidModal({ open: false, impact: null })}
                onConfirm={handleConfirmVoidWithReparse}
            />

        </>
    );
};

export default InvoiceDataSourceDetailDrawer;
